class StayAISubscriptions extends HTMLElement {
  /** Stay.ai selling plan app id — must match `stayai-pdp.liquid` (`where: 'app_id', …`). */
  static STAY_AI_SELLING_PLAN_APP_ID = "5859381";

  /** Ref-counted: history hook + one shared popstate (avoid duplicate listeners / heavy MutationObserver). */
  static _globalUrlSyncRefs = 0;
  static _origPushState = null;
  static _origReplaceState = null;
  static _onPopstateShared = null;

  static _notifyAllVariantFromUrl() {
    queueMicrotask(() => {
      document.querySelectorAll("purchase-options").forEach((el) => {
        if (typeof el.updateVariantID === "function") el.updateVariantID();
      });
    });
  }

  static ensureGlobalUrlSync() {
    StayAISubscriptions._globalUrlSyncRefs += 1;
    if (StayAISubscriptions._globalUrlSyncRefs > 1) return;

    StayAISubscriptions._onPopstateShared = () => StayAISubscriptions._notifyAllVariantFromUrl();
    window.addEventListener("popstate", StayAISubscriptions._onPopstateShared);

    StayAISubscriptions._origPushState = history.pushState.bind(history);
    StayAISubscriptions._origReplaceState = history.replaceState.bind(history);
    history.pushState = function (...args) {
      const ret = StayAISubscriptions._origPushState(...args);
      StayAISubscriptions._notifyAllVariantFromUrl();
      return ret;
    };
    history.replaceState = function (...args) {
      const ret = StayAISubscriptions._origReplaceState(...args);
      StayAISubscriptions._notifyAllVariantFromUrl();
      return ret;
    };
  }

  static releaseGlobalUrlSync() {
    if (StayAISubscriptions._globalUrlSyncRefs <= 0) return;
    StayAISubscriptions._globalUrlSyncRefs -= 1;
    if (StayAISubscriptions._globalUrlSyncRefs > 0) return;

    if (StayAISubscriptions._onPopstateShared) {
      window.removeEventListener("popstate", StayAISubscriptions._onPopstateShared);
      StayAISubscriptions._onPopstateShared = null;
    }
    if (StayAISubscriptions._origPushState) {
      history.pushState = StayAISubscriptions._origPushState;
      history.replaceState = StayAISubscriptions._origReplaceState;
      StayAISubscriptions._origPushState = null;
      StayAISubscriptions._origReplaceState = null;
    }
  }

  constructor() {
    super();
    this.productJSON = this.getAttribute("productData");
    this.product = JSON.parse(this.productJSON);
    this.initialVariant = this.getAttribute("currentVariantId");
    this.sellingPlanGroups = JSON.parse(this.getAttribute("sellingPlanGroups"));
    this.productFormId = this.getAttribute("productFormId");
    this.frequencyVisible = this.getAttribute("frequencyVisible");
  }
  connectedCallback() {
    this.initWidget();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this._variantPollId != null) {
      clearInterval(this._variantPollId);
      this._variantPollId = null;
    }
    if (this._onPageshow) {
      window.removeEventListener("pageshow", this._onPageshow);
      this._onPageshow = null;
    }
    StayAISubscriptions.releaseGlobalUrlSync();
  }

  /** Selling plan groups for Stay.ai only (defense if `sellingPlanGroups` attr ever includes other apps). */
  getStaySellingPlanGroups() {
    const want = String(StayAISubscriptions.STAY_AI_SELLING_PLAN_APP_ID);
    return (this.sellingPlanGroups || []).filter((g) => String(g.app_id) === want);
  }

  /**
   * Stay-filtered variant allocations as shallow copies, each with `groupFirstSellingPlanName`:
   * matching selling plan group’s `selling_plans[0].name` (same group as `selling_plan_group_id`).
   */
  getStaySellingPlanAllocations(variant = this.currentVariant) {
    const stayGroups = this.getStaySellingPlanGroups();
    const stayGroupIds = new Set(stayGroups.map((g) => g.id));
    const groupById = new Map(stayGroups.map((g) => [g.id, g]));

    return (variant?.selling_plan_allocations || [])
      .filter((spa) => stayGroupIds.has(spa.selling_plan_group_id))
      .map((spa) => {
        const group = groupById.get(spa.selling_plan_group_id);
        const groupFirstSellingPlanName = group?.selling_plans?.[0]?.name ?? "";
        return { ...spa, groupFirstSellingPlanName };
      });
  }

  /** First subscription-details list line: show first Stay allocation’s `groupFirstSellingPlanName` (delivery label). */
  refreshSubFrequencyTxt() {
    const v = this.currentVariant;
    if (!v) return;
    const allocs = this.getStaySellingPlanAllocations(v);
    const container =
      this.querySelector(
        ".stayai-subscription .stayai-widget .stayai-purchase-label__wrapper .stayai-subscription-details-container",
      ) || this.querySelector(".stayai-subscription-details-container");
    const ul = container?.querySelector("ul");
    const label = ul ? [...ul.children].find((el) => el.tagName === "LI") : null;
    if (!label) return;
    label.textContent = allocs[0]?.groupFirstSellingPlanName ?? "";
  }

  initWidget() {
    this.purchaseTypeSubscription = this.querySelector("[for='purchaseTypeSubscription']");
    this.purchaseTypeOneTime = this.querySelector("[for='purchaseTypeOneTime']");
    this.sellingPlanSelector = this.querySelector(".stayai-subscription-dropdown");
    this.subscriptionBox = this.querySelector("[data-retextion-subscription-box]");
    this.subscriptionBoxContainer = this.querySelector(".stayai-subscription-frequency-container");
    this.radioOneTime = this.querySelector('input[value="purchaseTypeOneTime"]');
    this.radioSubscription = this.querySelector('input[value="purchaseTypeSubscription"]');
    const selectedInitialVariant = this.product.variants.find((variant) => variant.id === Number(this.initialVariant));
    this.currentVariant = selectedInitialVariant || this.product.variants[0];
    this.refreshSubFrequencyTxt();
    this.updateSellingPlans();
  }
  // Set up event listeners for the purchase type, frequency and variant selectors
  setupEventListeners() {
    const purchaseTypeRadioButtons = this.querySelectorAll('input[name="purchaseType"]');
    // Handle purchase type change
    purchaseTypeRadioButtons.forEach((radio) =>
      radio.addEventListener("change", () => {
        this[radio.value === "purchaseTypeSubscription" ? "showSubscriptionBox" : "hideSubscriptionBox"]();
        this.updateProductPrice(this.sellingPlanSelector.value);
      }),
    );
    // Handle frequency selector change
    this.sellingPlanSelector.addEventListener("change", (e) => {
      this.updateSellingPlanInput(e.target.value);
      this.updateProductPrice(e.target.value);
    });
    // Variant from URL: popstate + pushState/replaceState (no document MutationObserver)
    StayAISubscriptions.ensureGlobalUrlSync();
    this._variantPollId = window.setInterval(() => this.syncVariantFromFormIfNeeded(), 400);
    // Handle browser back/forward navigation (per instance — restores this widget’s UI)
    this._onPageshow = (event) => {
      if (event.persisted || performance.getEntriesByType("navigation")[0]?.type === "back_forward") {
        this.isSubscriptionOptionSelected()
          ? this.querySelector("label[for='purchaseTypeSubscription']").click()
          : this.querySelector("label[for='purchaseTypeOneTime']").click();
        this.updateSellingPlans();
      }
    };
    window.addEventListener("pageshow", this._onPageshow);
  }
  // Get variant ID from the URL and update the variant
  updateVariantID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("variant")) return;
    const variantId = Number(urlParams.get("variant"));
    if (variantId && variantId !== this.currentVariant.id) {
      this.currentVariant = this.product.variants.find((v) => v.id === variantId);
      this.refreshSubFrequencyTxt();
      this.updateSellingPlans();
    }
  };
  // Populate the subscription frequency selector with selling plans for the current variant
  updateSellingPlans() {
    const stayWidget = this.querySelector(`.stayai-subscriptions`);
    if (!stayWidget) return;
    // Stay.ai groups only; exclude bundle-type groups; map by group id
    const groupMap = new Map(
      this.getStaySellingPlanGroups()
        .filter((g) => !g.options[0].name.toLowerCase().includes("bundle"))
        .map((g) => [g.id, g]),
    );
    const sellingPlans = this.getStaySellingPlanAllocations()
      .map((spa) => groupMap.get(spa.selling_plan_group_id)?.selling_plans.find((sp) => sp.id === spa.selling_plan_id))
      .filter(Boolean);
    if (sellingPlans.length === 0) {
      this.updateSellingPlanInput("");
      stayWidget.classList.toggle("is-visible", false);
      console.log("No SPs, hiding widget");
      return;
    } else stayWidget.classList.toggle("is-visible", true);
    // Clear existing options and populate dropdown
    this.sellingPlanSelector.innerHTML = sellingPlans
      .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
      .join("");
    if (!this.isSubscriptionOptionSelected()) {
      this.addSelectFrequencyOption();
      this.updateSellingPlanInput("");
    } else {
      this.updateSellingPlanInput(sellingPlans[0]?.id || "");
    }
    // Show or hide the subscription frequency selector
    this.subscriptionBox.classList.toggle("is-visible", this.isSubscriptionOptionSelected());
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", this.isSubscriptionOptionSelected());
    // Update prices
    this.updateProductPrice(
      this.isSubscriptionOptionSelected() ? this.sellingPlanSelector.value : sellingPlans[0]?.id || "",
    );
  }
  // Add "Select frequency" option to the frequency selector
  addSelectFrequencyOption() {
    if (!this.sellingPlanSelector.querySelector('option[value=""]')) {
      this.sellingPlanSelector.insertAdjacentHTML("afterbegin", '<option value="" selected>Select frequency</option>');
    }
  }
  // Show the subscription frequency selector
  showSubscriptionBox() {
    this.subscriptionBox.classList.toggle("stayai-disable", false);
    this.subscriptionBox.classList.toggle("is-visible", true);
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", true);
    this.purchaseTypeSubscription.classList.toggle("is-selected", true);
    this.purchaseTypeOneTime.classList.toggle("is-selected", false);
    this.sellingPlanSelector.querySelector('option[value=""]')?.remove();
    this.updateSellingPlanInput(this.sellingPlanSelector.value);
  }
  // Hide the subscription frequency selector
  hideSubscriptionBox() {
    this.addSelectFrequencyOption();
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("is-visible", false);
    this.subscriptionBox.classList.toggle("is-visible", false);
    this.subscriptionBox.classList.toggle("disable", true);
    this.purchaseTypeSubscription.classList.toggle("is-selected", false);
    this.purchaseTypeOneTime.classList.toggle("is-selected", true);
    this.updateSellingPlanInput("");
  }
  // Check if the subscription option is selected
  isSubscriptionOptionSelected() {
    return this.purchaseTypeSubscription.classList.contains("is-selected");
  }
  
  /**
   * matching logic id: cclkDbmDJtLk0poI 
   * First numeric token in variant title (matches Liquid parsing), e.g. "2packs" → 2, "Mini Dose - 1.5mg" → 1.5 */
  parseVariantUnitQtyFromTitle(title) {
    const str = String(title ?? "");
    let i = 0;
    while (i < str.length) {
      const ch = str[i];
      if (ch >= "0" && ch <= "9") break;
      i++;
    }
    if (i >= str.length) return 1;
    let num = "";
    let dot = false;
    while (i < str.length) {
      const c = str[i];
      if (c >= "0" && c <= "9") {
        num += c;
        i++;
        continue;
      }
      if (c === "." && !dot) {
        dot = true;
        num += c;
        i++;
        continue;
      }
      break;
    }
    const q = parseFloat(num);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  getProductForm() {
    let productForm = document.querySelector(
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
        'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"]:not(.installment)',
    );
    if (this.productFormId !== "") productForm = document.querySelector(this.productFormId) || productForm;
    return productForm || null;
  }

  /**
   * Theme uses #variantID (dropdowns) and/or #swatchID (swatches). Swatch script only updates #swatchID;
   * #variantID can stay stale, so never use the generic first input[name="id"] when both exist.
   */
  getVariantIdInput() {
    const form = this.getProductForm();
    if (!form) return null;
    const swatchInput = form.querySelector("#swatchID");
    const variantInput = form.querySelector("#variantID");
    if (swatchInput) return swatchInput;
    if (variantInput) return variantInput;
    return form.querySelector('input[name="id"], select[name="id"]');
  }

  /** Theme may set #variantID / name=id without a URL change; keep this.currentVariant in sync with Liquid variant_unit_qty rules. */
  syncVariantFromFormIfNeeded() {
    const input = this.getVariantIdInput();
    if (!input?.value) return;
    const vid = Number(input.value);
    if (!vid || Number.isNaN(vid) || vid === this.currentVariant.id) return;
    const next = this.product.variants.find((v) => v.id === vid);
    if (!next) return;
    this.currentVariant = next;
    this.refreshSubFrequencyTxt();
    this.updateSellingPlans();
  }

  /**
   * Per-unit amounts use the same qty as Liquid variant_unit_qty (first number in this.currentVariant.title).
   * Updates BOTH [data-stayai-subscription-price] and [data-stayai-onetime-price] .stayai-unit-price-value.
   */
  updateUnitPriceDisplays(allocationForUnitPrice) {
    const subscriptionLblPrice = this.querySelector("[data-stayai-subscription-price]");
    const oneTimeLblPrice = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionUnitEl = subscriptionLblPrice?.querySelector(".stayai-unit-price-value");
    const oneTimeUnitEl = oneTimeLblPrice?.querySelector(".stayai-unit-price-value");
    const qty = this.parseVariantUnitQtyFromTitle(this.currentVariant?.title);
    if (subscriptionUnitEl) {
      if (allocationForUnitPrice && qty > 0) {
        subscriptionUnitEl.textContent = this.formatMoney(Math.round(allocationForUnitPrice.price / qty));
      } else {
        subscriptionUnitEl.textContent = "";
      }
    }
    if (oneTimeUnitEl) {
      if (qty > 0 && this.currentVariant?.price != null) {
        let outputPrice = Math.round(this.currentVariant.price / qty);      
        oneTimeUnitEl.textContent = this.formatMoney(outputPrice);
      } else {
        oneTimeUnitEl.textContent = "";
      }
    }
  }

  /** First selling_plan_allocation for this variant in a non-bundle group (same filter as updateSellingPlans). */
  getFirstValidSellingPlanAllocation() {
    const allocations = this.getStaySellingPlanAllocations();
    if (!allocations?.length) return undefined;
    const groupMap = new Map(
      this.getStaySellingPlanGroups()
        .filter((g) => !g.options[0].name.toLowerCase().includes("bundle"))
        .map((g) => [g.id, g]),
    );
    for (const spa of allocations) {
      const plan = groupMap.get(spa.selling_plan_group_id)?.selling_plans.find((sp) => sp.id === spa.selling_plan_id);
      if (plan) return spa;
    }
    return undefined;
  }

  //Add selling plan input to form with specified selling plan ID
  updateSellingPlanInput(sellingPlanId) {
    const productForm = this.getProductForm();
    if (!productForm) return;

    // Find or create the hidden input for selling plan
    let sellingPlanInput = productForm.querySelector("input[name='selling_plan']");
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      sellingPlanInput.value = sellingPlanId;
      productForm.appendChild(sellingPlanInput);
    } else {
      sellingPlanInput.value = sellingPlanId;
    }
    // Dispatch event with the selling plan value
    this.fireStayAiEvent("stay-ai-subscription-changed", sellingPlanId);
  }
  updateProductPrice(sellingPlanId) {
    const stayAllocations = this.getStaySellingPlanAllocations();
    const selectedSellingPlanAllocation = stayAllocations.find((spa) => spa.selling_plan_id == sellingPlanId);
    const allocationForUnitPrice =
      selectedSellingPlanAllocation || this.getFirstValidSellingPlanAllocation();
    const sellingPlan = this.getStaySellingPlanGroups()
      .find((group) => group.selling_plans.some((sp) => sp.id == sellingPlanId))
      ?.selling_plans.find((sp) => sp.id == sellingPlanId);
    const oneTimeLblPrice = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionLblPrice = this.querySelector("[data-stayai-subscription-price]");
    const subscriptionLblCompareAt =
      subscriptionLblPrice?.querySelector(".stayai-sub-price-container s") || subscriptionLblPrice?.querySelector("s");
    const subscriptionLblPriceValue = subscriptionLblPrice?.querySelector(".stayai-sub-price-value");
    const savingLbl = this.querySelector(".subscription-savings");
    const onetimePrice = this.formatMoney(this.currentVariant.price);
    const compareAtPrice = this.formatMoney(this.currentVariant.compare_at_price);
    let subscriptionPrice = onetimePrice;

    // One-time row: .stayai-otp-price-* (stayai-pdp.liquid); legacy flat [data-stayai-onetime-price] fallback
    if (oneTimeLblPrice) {
      const otp = oneTimeLblPrice.querySelector(".stayai-otp-price-container");
      if (otp) {
        const oneTimeCompareAt = otp.querySelector("s");
        const oneTimeValue = otp.querySelector(".stayai-otp-price-value");
        if (oneTimeValue) oneTimeValue.textContent = onetimePrice;
        if (oneTimeCompareAt) {
          if (compareAtPrice !== "$0.00" && compareAtPrice !== onetimePrice) {
            oneTimeCompareAt.textContent = compareAtPrice;
            oneTimeCompareAt.hidden = false;
          } else {
            oneTimeCompareAt.textContent = "";
            oneTimeCompareAt.hidden = true;
          }
        }
      } else if (oneTimeLblPrice.textContent !== onetimePrice) {
        oneTimeLblPrice.innerHTML =
          compareAtPrice !== "$0.00" && compareAtPrice !== onetimePrice
            ? `<s>${compareAtPrice}</s> ${onetimePrice}`
            : onetimePrice;
      }
    }

    // Update subscription price
    if (selectedSellingPlanAllocation) {
      subscriptionPrice = this.formatMoney(selectedSellingPlanAllocation.price);
      if (subscriptionLblPrice) {
        if (subscriptionLblPriceValue && subscriptionLblPriceValue.textContent !== subscriptionPrice) {
          subscriptionLblPriceValue.textContent = subscriptionPrice;
        }
        if (subscriptionLblCompareAt) {
          if (subscriptionPrice !== onetimePrice) {
            subscriptionLblCompareAt.textContent = onetimePrice;
            subscriptionLblCompareAt.classList.add("fullPrice");
            subscriptionLblCompareAt.hidden = false;
          } else {
            subscriptionLblCompareAt.textContent = "";
            subscriptionLblCompareAt.classList.remove("fullPrice");
            subscriptionLblCompareAt.hidden = true;
          }
        }
      }
      // Calculate and update savings label
      let discount = this.currentVariant.price - selectedSellingPlanAllocation.price;
      if (discount > 0) {
        if (sellingPlan?.price_adjustments[0].value_type === "percentage") {
          discount = `${sellingPlan.price_adjustments[0].value}%`;
        } else {
          discount = this.formatMoney(discount);
        }
        if (savingLbl && savingLbl.textContent !== discount) savingLbl.textContent = discount;
      } else if (savingLbl) {
        savingLbl.textContent = "";
      }
    } else if (savingLbl) {
      savingLbl.textContent = "";
    }

    this.updateUnitPriceDisplays(allocationForUnitPrice);
  }
  // Toggle checked state based on the currently checked radio button
  toggleRadioButtons() {
    this.radioOneTime.checked = !this.radioOneTime.checked;
    this.radioSubscription.checked = !this.radioSubscription.checked;
  }
  // Format money based on Shopify's active currency
  formatMoney(money) {
    const { Shopify, theme } = window;
    const currency = Shopify?.currency?.active || "USD";
    const locale = Shopify?.locale || "en";
    const hasShopifyFormatMoney = Shopify && typeof Shopify?.formatMoney === "function";
    const hasThemeUtilsFormatMoney = theme?.utils && typeof theme?.utils?.formatMoney === "function";
    const formattedMoney = (money / 100).toFixed(2);
    let formatted;
    const currencyFormatter = new Intl.NumberFormat(locale, {
      currency,
      style: "currency",
    });
    if (!hasShopifyFormatMoney && !hasThemeUtilsFormatMoney) {
      return currencyFormatter.format(Number(formattedMoney));
    }
    formatted = hasShopifyFormatMoney ? Shopify.formatMoney(money) : theme.utils.formatMoney(money);
    return formatted || currencyFormatter.format(Number(formattedMoney));
  }
  fireStayAiEvent(eventName, sellingPlanId) {
    const event = new CustomEvent(eventName, {
      detail: {
        sellingPlanId: sellingPlanId,
      },
    });
    window.dispatchEvent(event);
  }
}
// Define the custom element
customElements.define("purchase-options", StayAISubscriptions);